import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import AppPaths from '../../../../AppPaths';
import { useHistory, useLocation } from 'react-router-dom';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';
import { petsWithActiveSubscriptionQuery, applyMultidogDiscountMutation } from '../../../../graphql-operations';
import { gqlTypes } from '../../../../types';
import { useQuery, useMutation } from '@apollo/client';
import { getLoadingOrErrorElement } from '../../../../lib/util';
import classNames from 'classnames';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import { inAppCancellation as events } from '../../../../lib/analytics/events';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import { BaseInAppCancellationState } from '../util/types';

function monthlyBillingCandenceString(renewalMonths: number, t: TFunction) {
  switch (renewalMonths) {
    case 1:
      return t('billed_monthly');
    case 3:
      return t('billed_quarterly');
    case 6:
      return t('billed_semi_annually');
    case 12:
      return t('billed_annually');
    default:
      return t('billed_every_months_months', { months: renewalMonths });
  }
}
function yearlyBillingCandenceString(renewalMonths: number, t: TFunction) {
  switch (renewalMonths) {
    case 12:
      return t('billed_annually');
    default:
      return t('billed_every_years_years', { years: renewalMonths / 12 });
  }
}

function subscriptionLengthString(t: TFunction, renewalMonths?: number | null, renewalYears?: number | null) {
  if (renewalMonths && renewalMonths >= 12 && !renewalYears) {
    renewalYears = Math.round(renewalMonths / 12);
  }

  if (renewalYears !== null && renewalYears !== undefined) {
    return t('years_years', { years: renewalYears, count: renewalYears });
  } else if (renewalMonths !== null && renewalMonths !== undefined) {
    return t('months_months', { months: renewalMonths, count: renewalMonths });
  } else {
    return t('unknown');
  }
}

function PetCurrentSubscriptionDetails(pet: any, idx: number, t: TFunction) {
  const subDetails = pet.device?.subscription?.subscriptionOption ?? null;
  if (!subDetails) {
    return null;
  }
  const currentRate = subDetails.priceInCents / 100;
  const discountedRate = Math.round((subDetails.priceInCents * 0.95) / 100);
  const renewalMonths = subDetails.renewalMonths;

  let billingCadenceString = '';
  switch (subDetails.billingCadence) {
    case gqlTypes.BillingCadence.MONTH:
      billingCadenceString = monthlyBillingCandenceString(renewalMonths, t);
      break;
    case gqlTypes.BillingCadence.YEAR:
      billingCadenceString = yearlyBillingCandenceString(renewalMonths, t);
      break;
  }

  const subscriptionLength = subscriptionLengthString(t, subDetails.renewalMonths, subDetails.renewalYears);

  return (
    <div className={styles.multidogDiscount} key={idx}>
      <div className={classNames(styles.column, styles.avatar)}>
        {pet.photos.first?.image?.fullSize && <img alt={pet.name} src={pet.photos.first.image.fullSize} />}
      </div>
      <div className={classNames(styles.column)}>
        <div className={classNames(styles.row, styles.name)}>{pet.name}</div>
        <div className={classNames(styles.row, styles.name)}>{subscriptionLength}</div>
        <div className={classNames(styles.row)}>{billingCadenceString}</div>
        <div className={classNames(styles.row)}>{t('multi_dog_discount_5_percent')}</div>
      </div>
      <div className={classNames(styles.column, styles.prices)}>
        <div className={classNames(styles.row)}>&nbsp;</div>
        <div className={classNames(styles.row)}>&nbsp;</div>
        <div className={classNames(styles.row, styles.current)}>
          {'$'}
          {currentRate}
        </div>
        <div className={classNames(styles.row, styles.discounted)}>
          {'$'}
          {discountedRate}
        </div>
      </div>
    </div>
  );
}

export default function MultidogDiscount() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<BaseInAppCancellationState>();
  const { subscriptionCategory } = location.state;
  const { device } = usePurchaseSubscriptionContext();
  events.pageLoad({ petId: device.pet!.id }, 'Multidog Discount');

  const activePetsQuery = useQuery<gqlTypes.ECOMMERCE_petsWithActiveSubscription>(petsWithActiveSubscriptionQuery, {});
  const zendeskMutation = useZendeskCancellationTicket({
    analyticsEvent: events.multidogDiscountCancelClicked,
    selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
    device,
    subscriptionCategory,
  });
  const [multidogDiscountMutation, multidogDiscountState] = useMutation<gqlTypes.ECOMMERCE_applyMultidogDiscount>(
    applyMultidogDiscountMutation,
    {
      onCompleted: (data) => {
        if (data.applyMultidogDiscount?.success) {
          events.multidogDiscountApplied({
            petId: device.pet?.id,
          });
          history.push({
            pathname: AppPaths.Subscription.MultidogDiscountConfirmation(device.moduleId),
            search: window.location.search,
          });
        } else {
          throw new Error(t('something_went_wrong'));
        }
      },
    },
  );

  const loadingOrErrorElement =
    zendeskMutation.loading ||
    getLoadingOrErrorElement(
      activePetsQuery.loading || multidogDiscountState.loading,
      activePetsQuery.error || multidogDiscountState.error,
    );
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  const handleCancel = () => {
    events.multidogDiscountCancelClicked({ petId: device.pet!.id });
    zendeskMutation.mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          cancellationReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE,
        },
      },
    });
  };

  const petsWithActiveSubscription = activePetsQuery.data?.petsWithActiveSubscription ?? [];
  const isMultidogHousehold = petsWithActiveSubscription.length > 1;
  if (!isMultidogHousehold) {
    throw new Error(t('this_page_should_only_be_shown_to_multi_dog_households'));
  }
  const offerFreeMonth = device.shouldOfferFreeMonthDuringCancellation ?? false;
  const isS3Device = device.currentCollarModel === 'S3';
  const offerS3Downgrade = isS3Device && !!device.shouldOfferDowngradeDuringCancellation;

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.multidogDiscountBack({ petId: device.pet?.id })}
          closeButtonAnalyticsEvent={() => events.multidogDiscountClose({ petId: device.pet?.id })}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.content}>
            <div className={styles.largeHeader}>
              <h1>
                <Trans i18nKey="discount_for_your_pack" components={{ br: <br /> }} />
              </h1>
            </div>
            <p>{t('enjoy_a_5_percent_discount_on_all_your_memberships_as_a_multi_dog_customer')}</p>
            {petsWithActiveSubscription.map((pet, i) => PetCurrentSubscriptionDetails(pet, i, t))}
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            className={styles.button}
            onClick={() => {
              multidogDiscountMutation({
                variables: {
                  input: {
                    petId: device.pet?.id,
                  },
                },
              });
            }}
          >
            {t('claim_multi_dog_discount')}
          </Button>

          {offerS3Downgrade || offerFreeMonth ? (
            <Button
              className={styles.button}
              tertiary={true}
              onClick={() => {
                if (!device.subscription?.id) {
                  throw new Error(t('something_went_wrong'));
                }

                events.multidogDiscountCancelClicked({ petId: device.pet!.id });

                if (offerS3Downgrade) {
                  history.push({
                    pathname: AppPaths.Subscription.DowngradeS3(device.moduleId),
                    state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE },
                    search: window.location.search,
                  });
                } else if (offerFreeMonth) {
                  history.push({
                    pathname: AppPaths.Subscription.FreeMonthOffer(device.moduleId),
                    state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.TOO_EXPENSIVE },
                    search: window.location.search,
                  });
                } else {
                  throw new Error(t('something_went_wrong'));
                }
              }}
            >
              {t('continue_with_cancellation')}
            </Button>
          ) : (
            <CancelConfirmationModal
              onContinue={() => handleCancel()}
              pageName={t('multidog_discount')}
              petId={device.pet!.id}
            />
          )}
        </div>
      </div>
    </>
  );
}
